import { isElectron } from '@/renderer/util';
import { useAppSelector } from '@hooks/useRedux';
import { selectCurrentUser } from '@store/auth/authSelectors';
import { Monitor } from 'lucide-react';
import React, { useState } from 'react';
import { Button } from './Button';
import { Modal } from './Modal';

const DESKTOP_APP_PROMPT_SEEN_KEY = 'smartpos_desktop_app_prompt_seen';
const DESKTOP_APP_DOWNLOAD_URL = 'https://www.smartposcloud.app/download';

export function hasSeenDesktopAppPrompt(): boolean {
  try {
    return localStorage.getItem(DESKTOP_APP_PROMPT_SEEN_KEY) === 'true';
  } catch {
    return false;
  }
}

function rememberDesktopAppPromptSeen(): void {
  try {
    localStorage.setItem(DESKTOP_APP_PROMPT_SEEN_KEY, 'true');
  } catch {
    // localStorage unavailable — non-fatal; the dialog just reappears next visit.
  }
}

interface DesktopAppDownloadDialogProps {
  /** Suppress rendering even if this dialog would otherwise be eligible to
   * show — used to keep root-level modals from ever visually colliding.
   * See App.tsx's priority chain: SystemClockWarning > DesktopAppDownloadDialog
   * > FeatureReleaseModal. */
  suppressed?: boolean;
}

/**
 * Shown once ever to web-app users, pointing them to the downloadable
 * desktop app. Never renders in the Electron build.
 *
 * Mount once near the root of the app (App.tsx).
 */
export const DesktopAppDownloadDialog: React.FC<
  DesktopAppDownloadDialogProps
> = ({ suppressed = false }) => {
  const currentUser = useAppSelector(selectCurrentUser);
  const [isOpen, setIsOpen] = useState(() => !hasSeenDesktopAppPrompt());

  if (isElectron() || !currentUser || !isOpen || suppressed) return null;

  const handleDismiss = () => {
    rememberDesktopAppPromptSeen();
    setIsOpen(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleDismiss}
      title="Get the Desktop App"
      size="md"
    >
      <div className="space-y-6">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-pos-primary/10 text-pos-primary">
            <Monitor size={22} />
          </div>
          <p className="pt-1 text-sm leading-6 text-gray-700">
            Download smartPOS Cloud desktop app for easy, one-click access, no
            browser tab required. You can download it anytime from our website.
          </p>
        </div>

        <div className="flex justify-end gap-3 border-t border-gray-100 pt-4">
          <Button variant="outline" onClick={handleDismiss}>
            Maybe later
          </Button>
          <a
            href={DESKTOP_APP_DOWNLOAD_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleDismiss}
            className="inline-flex items-center justify-center rounded-lg bg-pos-primary px-4 py-2 text-sm font-semibold text-white transition-all duration-200 hover:bg-pos-secondary focus:outline-none focus:ring-2 focus:ring-pos-primary focus:ring-offset-2"
          >
            Download Desktop App
          </a>
        </div>
      </div>
    </Modal>
  );
};
